import React, { useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button, Input } from "reactstrap";
import swal from "sweetalert";

import { useDiet } from "../../../hooks/useDiet";
import { AppContext } from "../../../context/app-context";

import PowerBlue from "../../../assets/images/Dietplan-updated/powerwhite.svg";
import PowerBlueIOS from "../../../assets/images/Dietplan-updated/powerwhite-ios.svg";
import { PlanName } from "../../../constants";
import { getMobileOperatingSystem } from "../../../helper/mobile";
import PlanCategory from "./PlanCategory";

const availablePlans = [
  {
    id: 4,
    planType: 'Pro Lite Plan',
    planTypeTwo: '',
    price: {
      android: "₹2999",
      ios: "₹2999"
    },
    discount: `50% OFF`,
    oldPrice: {
      android: "₹5988",
      ios: "₹5988"
    },
    platforms: ['android'],
    value: PlanName.YEARLY_ELITE_PLAN,
    color: "linear-gradient(90deg, #8B5B2F 0%, #C48042 24.4%, #C48042 57.4%, #8B5B2F 79.5%)",
    placeholderText: 'Only ₹250 /month',
    isNew: true,
    isYearlyPlan: true,
    title: 'Pro Lite Plan',
    allowedVersion: {
      android: '7.1.0'
    },
    useGradientText: true,
    buttonText: "Buy Now",
    features: [
      "Count every calorie - stay on track, no excuses!",
      "Unlock powerful insights - see your progress, own your success!",
      "Your health, your rules - stay guided, stay unbeatable!",
      "Hydrate like a pro - track it, sip it, crush it!",
      "Estimated Hba1C - Know your numbers - take charge, stay ahead of diabetes!"
    ]
  },
  {
    id: 3,
    planType: 'Pro Lite Plan',
    planTypeTwo: '',
    price: {
      android: "₹299",
      ios: "₹299"
    },
    oldPrice: {
      android: "₹499",
      ios: "₹499"
    },
    platforms: ['ios'],
    value: PlanName.ELITE_PLAN,
    color: "linear-gradient(90deg, #8B5B2F 0%, #C48042 24.4%, #C48042 57.4%, #8B5B2F 79.5%)",
    placeholderText: 'Cancel anytime',
    isNew: true,
    title: 'Pro Lite Plan',
    notAllowedVersion: {
      android: '7.0.9'
    },
    allowedVersion: {
      android: '7.0.0',
      ios: '4.0.9'
    },
    useGradientText: true,
    buttonText: "Confirm subscription",
    features: [
      "Count every calorie - stay on track, no excuses!",
      "Unlock powerful insights - see your progress, own your success!",
      "Your health, your rules - stay guided, stay unbeatable!",
      "Hydrate like a pro - track it, sip it, crush it!",
      "Estimated Hba1C - Know your numbers - take charge, stay ahead of diabetes!"
    ]
  },
  {
    id: 1,
    planType: 'Generated AI Diet Plan',
    planTypeTwo: '',
    price: {
      ios: '₹699',
      android: '₹699'
    },
    oldPrice: {
      ios: '₹1999',
      android: '₹1999'
    },
    platforms: ['ios','android'],
    value: PlanName.AI_PLAN,
    color: "linear-gradient(90deg, #BA9825 -11.99%, #E7BF37 44.51%, #BA9825 105.72%)",
    placeholderText: 'Cancel anytime',
    title: 'Generated AI Diet Plan',
    isNew: true,
    allowedVersion: {
      android: '7.1.0'
    },
    useGradientText: false,
    buttonText: "Confirm subscription",
    features: [
      "Designed by our Proprietary AI",
      "Based on your health data on Dr Trust app",
      "Based on your dietary preferences",
      "Based on your health goals",
      "Food items popular in your region",
      "Unique plan tailored to your exact needs",
      {
        type: "highlight",
        label: "Pro Lite Plan",
        color: "linear-gradient(90deg, #8B5B2F 0%, #C48042 24.4%, #C48042 57.4%, #8B5B2F 79.5%)"
      }
    ]
  },
];

const Payment = ({
  categoryId,
  selectedPlanType,
  updatePlanType,
  setCategoryId,
}) => {
  const [state] = useContext(AppContext);
  const { makeDietPayment } = useDiet();
  const [isGoalSheetOpen, setIsGoalSheetOpen] = useState(false);

  localStorage.setItem("planType", categoryId);
  const dietPlanId = state?.existingDietplan?.dietPlanId;
  const osType = String(state?.user?.osType || "").toLowerCase();
  const detectedDevice = getMobileOperatingSystem();
  const device = ["android", "ios"].includes(detectedDevice)
    ? detectedDevice
    : (osType === "ios" ? "ios" : "android");

  const orderedPlans = availablePlans.filter((plan) => {
    const findPlatform = plan.platforms.find((p) => p === device);
    return !!findPlatform;
  });
  const selectedPlan = orderedPlans.find(p => p.value === selectedPlanType);

  const getSavedLabel = (option) => {
    if (option.value === PlanName.AI_PLAN) {
      return "+Pro Lite Plan";
    }

    const current = Number(
      String(option.price?.[device] || "").replace(/[^0-9]/g, "")
    );
    const old = Number(
      String(option.oldPrice?.[device] || "").replace(/[^0-9]/g, "")
    );

    if (!current || !old || old <= current) {
      return null;
    }

    return `₹${old - current} saved`;
  };

  const onClickMakePayment = (paymentType) => {
    if(!categoryId && selectedPlan?.planTypeTwo === "AI Diet Plan") {
      swal({
        className: "swal-height-width-change",
        title: "Select a Goal",
        text: "Please choose your goal to proceed.",
        buttons: "Okay"
      }).then(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
      });
      return;
    } 
    if (dietPlanId) {
      swal({
        className: "swal-height-width-change",
        title: "Diet plan exists",
        text: "You can only be on one diet plan at a time. You can buy a new diet plan post expiry of existing plan.",
        buttons: "Okay",
      });
    } else {
      if (osType) {
        makeDietPayment(selectedPlanType, categoryId, paymentType);
      } else {
        swal({
          className: "swal-height-width-change",
          title: "Something went wrong",
          text: "Please try starting it again or contact customer care",
          buttons: "Okay",
        });
      }
    }
  };

  const handleOnChange = (value) => {
    updatePlanType(value);
    if (typeof window !== "undefined") {
      const currentY = window.scrollY || window.pageYOffset || 0;
      // Avoid unnecessary "flicker" when we're already at the top
      if (currentY > 0) {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    }
  };

  const getPrimaryButtonLabel = (pType) => {
    if (!pType) return "";

    if (pType === PlanName.AI_PLAN) {
      return "Select your goal";
    }

    if (
      pType === PlanName.ELITE_PLAN ||
      pType === PlanName.YEARLY_ELITE_PLAN
    ) {
      return "Buy now";
    }

    if (pType === PlanName.SHEET_PLAN) {
      return "Continue";
    }

    return selectedPlan?.isYearlyPlan ? "Buy Now" : "Confirm subscription";
  };

  useEffect(() => {
    if (typeof document === "undefined") return;
    if (!isGoalSheetOpen) return;

    const body = document.body;
    const prevOverflow = body.style.overflow;
    const prevTouchAction = body.style.touchAction;

    body.style.overflow = "hidden";
    body.style.touchAction = "none";

    return () => {
      body.style.overflow = prevOverflow;
      body.style.touchAction = prevTouchAction;
    };
  }, [isGoalSheetOpen]);

  useEffect(() => {
    if (!selectedPlan && orderedPlans.length) {
      updatePlanType(orderedPlans[0].value);
    }
  }, [selectedPlan, updatePlanType, orderedPlans]);

  return (
    <div>
    {orderedPlans.length > 0
      ? (<>
        <div
          style={{
            background: "#f5f3f3",
            borderRadius: "18px 18px 0 0",
            padding: "15px 14px 12px",
            margin: "0",
          }}
        >
          {orderedPlans.map((option) => {
            const isSelected = option.value === selectedPlanType;
            const isGoldPlan = option.value === PlanName.AI_PLAN;
            const monthsValue = option?.isYearlyPlan ? "12" : "1";
            const monthsLabel = Number(monthsValue) > 1 ? "MONTHS" : "MONTH";
            const savedLabel = getSavedLabel(option);
            const isSilverPlanOption =
              option.value === PlanName.ELITE_PLAN ||
              option.value === PlanName.YEARLY_ELITE_PLAN;

            return (
              <label
                key={option.value}
                htmlFor={option.value}
                className={`payment-plan-option ${isSelected ? "payment-plan-option--selected" : ""}`}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "6px 8px",
                  borderRadius: "12px",
                  marginBottom: "6px",
                  background: "#fafafa",
                  border: isSelected
                    ? "1px solid #2451ff"
                    : "1px solid #d1d1d1",
                  boxShadow: isSelected
                    ? "0px 1.14px 19.29px 0px #00000040"
                    : "none",
                  cursor: "pointer",
                  columnGap: "10px",
                  rowGap: "8px",
                  flexWrap: "wrap",
                  transition:
                    "transform 220ms ease, box-shadow 220ms ease, border-color 220ms ease, background-color 220ms ease",
                  transform: isSelected ? "scale(1.01)" : "scale(1)",
                }}
                onClick={() => handleOnChange(option.value)}
              >
                <Input
                  id={option.value}
                  type="radio"
                  className="d-none"
                  value={option.value}
                  checked={isSelected}
                  readOnly
                />

                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    minWidth: 0,
                    flex: 1,
                  }}
                >
                  <div
                    style={{
                      textAlign: "center",
                      flexShrink: 0,
                      minWidth: "42px",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "clamp(20px, 6vw, 28px)",
                        fontWeight: 700,
                        color: isGoldPlan ? "#b6893f" : "#888",
                        lineHeight: "clamp(24px, 6.6vw, 34px)",
                      }}
                    >
                      <span
                        className={
                          isSilverPlanOption
                            ? "silver-plan-gradient-text"
                            : isGoldPlan
                            ? "gold-plan-gradient-text"
                            : ""
                        }
                      >
                        {monthsValue}
                      </span>
                    </div>
                    <div
                      style={{
                        fontSize: "9px",
                        fontWeight: 600,
                        color: isGoldPlan ? "#b6893f" : "#888",
                      }}
                    >
                      <span
                        className={
                          isSilverPlanOption
                            ? "silver-plan-gradient-text"
                            : isGoldPlan
                            ? "gold-plan-gradient-text"
                            : ""
                        }
                      >
                        {monthsLabel}
                      </span>
                    </div>
                  </div>

                  <div style={{ minWidth: 0, flexShrink: 1 }}>
                    <div
                      className={
                        isGoldPlan ? "payment-plan-title payment-plan-title--ai" : "payment-plan-title"
                      }
                      style={{
                        fontSize: "clamp(14px, 4.8vw, 20px)",
                        fontWeight: 900,
                        color: isGoldPlan ? "#b6893f" : "#7b7b7b",
                        whiteSpace: "normal",
                        lineHeight: 1.1,
                      }}
                    >
                      <span
                        className={
                          isSilverPlanOption
                            ? "silver-plan-gradient-text"
                            : isGoldPlan
                            ? "gold-plan-gradient-text"
                            : ""
                        }
                      >
                        {option.planType}
                      </span>
                    </div>
                    {isGoldPlan && (
                      <img
                        src={device === "ios" ? PowerBlueIOS : PowerBlue}
                        alt="Powered by Dr Trust Intelligence"
                        style={{
                          marginTop: "2px",
                          maxWidth: "100%",
                          height: "20px",
                        }}
                        loading="lazy"
                      />
                    )}
                  </div>
                </div>

                <div
                  style={{
                    textAlign: "center",
                    flexShrink: 0,
                    minWidth: "86px",
                  }}
                >
                  {savedLabel && (
                    <div
                      className={
                        option.value === PlanName.AI_PLAN
                          ? "silver-plan-badge"
                          : "saved-label-badge"
                      }
                    >
                      {savedLabel}
                    </div>
                  )}
                  <div
                    style={{
                      fontSize: "clamp(13px, 4vw, 16px)",
                      fontWeight: 700,
                      color: "#1f49ff",
                    }}
                  >
                    {option.price[device]}
                    {option?.isYearlyPlan ? "/Yr*" : "/Mo*"}
                  </div>
                  <div
                    style={{
                      textDecoration: "line-through",
                      color: "#9c9c9c",
                      fontSize: "clamp(8px, 2.6vw, 9px)",
                    }}
                  >
                    {option.oldPrice[device]}
                    {option?.isYearlyPlan ? "/Yr*" : "/Mo*"}
                  </div>
                </div>
              </label>
            );
          })}
          <div style={{ marginTop: "8px" }}>
            <Button
              key={selectedPlan?.value || "confirm-btn"}
              className="risk-btn mb-2 w-100"
              onClick={() => {
                if (!selectedPlan) return;
                if (selectedPlanType === PlanName.AI_PLAN) {
                  setIsGoalSheetOpen(true);
                  return;
                }
                onClickMakePayment("");
              }}
              disabled={!selectedPlan}
              style={{
                borderRadius: "36px",
                fontSize: "13px",
                fontWeight: 600,
                paddingTop: "6px",
                paddingBottom: "6px",
              }}
            >
              {getPrimaryButtonLabel(selectedPlanType)}
            </Button>
            <div className="text-center">
              <Link
                className="payment_couponcode"
                to="/dietPlan/couponCodeActivation"
              >
                Have a coupon Code
              </Link>
            </div>
          </div>
        </div>

        {isGoalSheetOpen && (
          <div
            className="goal-popup-overlay"
            onTouchMove={(e) => e.preventDefault()}
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setIsGoalSheetOpen(false);
              }
            }}
          >
            <div className="goal-popup-modal">
              <div className="goal-popup-header">
                <h2 className="goal-popup-heading">Choose Your Goal</h2>
                <button
                  type="button"
                  className="goal-popup-close"
                  onClick={() => setIsGoalSheetOpen(false)}
                  aria-label="Close"
                >
                  ×
                </button>
              </div>
              <div className="goal-popup-body">
                <PlanCategory
                  categoryId={categoryId}
                  setCategoryId={setCategoryId}
                  disabled={false}
                />
              </div>
              <div className="goal-popup-footer">
                <Button
                  className="risk-btn w-100"
                  onClick={() => {
                    if (!categoryId) {
                      swal({
                        className: "swal-height-width-change",
                        title: "Select a Goal",
                        text: "Please choose your goal to proceed.",
                        buttons: "Okay",
                      });
                      return;
                    }
                    setIsGoalSheetOpen(false);
                    onClickMakePayment("");
                  }}
                >
                  Confirm Subscription
                </Button>
              </div>
            </div>
          </div>
        )}
      </>)
      : null
    }
    </div>
  );
};
export default Payment;
